import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BiUserPlus, BiLogInCircle, BiLoader, BiLock } from 'react-icons/bi';
import { authService } from '../services/authService';
import Alert from '../components/Alert';
import EmailVerification from '../components/EmailVerification';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { registerSchema, cleanPhoneNumber, type RegisterFormData } from '../utils/validationSchemas';
import { buildAuthUrl, getRedirectUrl } from '../utils/url';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [showVerification, setShowVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    // Support both 'email' and 'adminEmail' query parameters for backward compatibility
    const emailParam = searchParams.get('email') || searchParams.get('adminEmail');
    if (emailParam) {
      setAdminEmail(emailParam);
    }
  }, [searchParams]);

  const initialValues: RegisterFormData = {
    firstName: '',
    lastName: '',
    email: adminEmail || '',
    phone: '',
    password: '',
    confirmPassword: '',
  };

  const handleSubmit = async (values: RegisterFormData, { setSubmitting }: any) => {
    setMessage('');
    setMessageType('info');
    setIsLoading(true);

    try {
      const response = await authService.register({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone
      });

      if (response.success) {
        setRegisteredEmail(values.email);
        setShowVerification(true);
      } else {
        setMessage(response.message || 'Registration failed. Please try again.');
        setMessageType('error');
      }
    } catch (error: any) {
      setMessage(error.message || 'An unexpected error occurred during registration.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (showVerification) {
    return (
      <EmailVerification
        email={registeredEmail}
        redirectUrl={getRedirectUrl(searchParams)}
        onResend={async () => {
          try {
            const response = await authService.verifyEmail(registeredEmail, '');
            if (response.success) {
              setMessage('Verification email resent successfully!');
              setMessageType('success');
            } else {
              setMessage(response.message || 'Failed to resend verification email.');
              setMessageType('error');
            }
          } catch (error: any) {
            setMessage(error.message || 'An error occurred while resending verification email.');
            setMessageType('error');
          }
        }}
        onBack={() => setShowVerification(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Create Account</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Join us and start managing your agencies.</p>
        </div>

        {message && <Alert type={messageType} message={message} onClose={() => setMessage('')} />}

        <Formik
          key={adminEmail || 'default'} // Force re-render when adminEmail changes
          initialValues={initialValues}
          validationSchema={registerSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="firstName">
                    First Name
                  </label>
                  <Field
                    id="firstName"
                    name="firstName"
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                    placeholder="John"
                  />
                  <ErrorMessage name="firstName" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="lastName">
                    Last Name
                  </label>
                  <Field
                    id="lastName"
                    name="lastName"
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                    placeholder="Doe"
                  />
                  <ErrorMessage name="lastName" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="email">
                  Email Address
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  disabled={!!adminEmail}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${adminEmail
                    ? 'bg-slate-50 dark:bg-slate-600 border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-300 cursor-not-allowed'
                    : 'bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white'
                    }`}
                  placeholder="you@example.com"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="phone">
                  Phone Number
                </label>
                <Field
                  id="phone"
                  name="phone"
                  type="tel"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="+1 (555) 123-4567"
                />
                <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="password">
                  Password
                </label>
                <Field
                  id="password"
                  name="password"
                  type="password"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="••••••••"
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <Field
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="••••••••"
                />
                <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isSubmitting || isLoading ? (
                    <>
                      <BiLoader className="animate-spin mr-2" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <BiUserPlus className="mr-2" />
                      Create Account
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        <div className="text-center text-sm text-slate-500 dark:text-slate-400">
          <p>
            Already have an account?{' '}
            <Link
              to={buildAuthUrl('/auth/login', getRedirectUrl(searchParams))}
              className="font-medium text-primary-600 hover:text-primary-500 flex items-center justify-center"
            >
              <BiLogInCircle className="mr-1" />
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
